import SwupFragmentPlugin from '@swup/fragment-plugin';
import type { Visit } from 'swup';
import type { MediaPlayerElement } from 'vidstack/elements';

export function movePlayers(visit: Visit) {
    return new Promise(async (resolve) => {
        const keepAliveComponents: NodeListOf<HTMLElement> =
            document.querySelectorAll(
                '#outlet .keepalive:has(media-player[data-playing])'
            )

        if (keepAliveComponents.length === 0) {
            resolve(false)
            return
        }

        // Close all existing floating players before creating new ones
        const existingFloatingPlayerKeys = Object.keys(window.floatingPlayers)

        for (const key of existingFloatingPlayerKeys) {
            // Keep the player that is being expanded back into its own page.
            if (window.floatingPlayers[key]?.source === visit.to.url) {
                continue
            }

            await window.floatingPlayers[key]?.close()
            window.floatingPlayers[key]?.destroy()
        }

        for (const component of keepAliveComponents) {
            const player = new FloatingPlayer(component)

            window.floatingPlayers[player.id] = player

            await player.startFloating()
        }

        resolve(true)
    })
}

class FloatingPlayer {
    private readonly el: HTMLElement
    private mediaPlayer: MediaPlayerElement
    private wrapper: HTMLElement
    private expandButton: HTMLButtonElement
    private closeButton: HTMLButtonElement
    public readonly source: string

    public id: string | number

    constructor(el: HTMLElement) {
        this.el = el
        this.mediaPlayer = el.querySelector('media-player') as MediaPlayerElement
        this.wrapper = document.getElementById('keepalive') as HTMLElement

        this.expandButton = el.querySelector(
            '.player-expand'
        ) as HTMLButtonElement
        this.closeButton = el.querySelector(
            '.player-close'
        ) as HTMLButtonElement

        this.id = el.dataset.id as string | number
        this.source = el.dataset.url as string

        this.init()
    }

    private init() {
        this.expandButton.addEventListener('click', async () => {
            if (this.isSourcePage) {
                await this.stopFloating()
                return
            }

            // Stop floating once the source page and its `#outlet` are
            // rendered.
            window.swup.hooks.once('page:view', async () => {
                if (this.isSourcePage) {
                    await this.stopFloating()
                }
            })

            // Navigate to the source page. The POST request renders the
            // page without its player, so the floating one can move in.
            window.swup.navigate(this.source, {
                method: 'POST',
                animate: false,
            })
        })

        this.closeButton.addEventListener('click', async () => {
            await this.close()

            if (this.isSourcePage) {
                this.reloadOutlet()
            }

            this.destroy()
        })
    }

    public async pauseMediaPlayer() {
        await this.mediaPlayer.pause()
    }

    public async startFloating() {
        this.el.style.setProperty('view-transition-name', 'move-player')

        const moveTransition = document.startViewTransition(() => {
            this.wrapper.append(this.el)
            // Clean up immediately after moving
            this.removeDuplicateControls()
        })

        await moveTransition.finished

        this.el.style.removeProperty('view-transition-name')

        this.injectSwupRules()
    }

    public async stopFloating() {
        // there's an open bug here: https://github.com/vidstack/player/issues/1191

        const outlet = document.getElementById('outlet')

        if (!outlet) return

        // Remove existing player with same ID from outlet to prevent duplicates
        const existingPlayer = outlet.querySelector(
            `.keepalive[data-id="${this.id}"]`
        )
        if (existingPlayer) {
            existingPlayer.remove()
        }

        this.el.style.setProperty('view-transition-name', 'move-player')

        const moveTransition = document.startViewTransition(() => {
            outlet.append(this.el)
            // Clean up immediately after moving
            this.removeDuplicateControls()
        })

        await moveTransition.finished

        this.el.style.removeProperty('view-transition-name')

        this.removeSwupRules()
    }

    public async close() {
        await this.mediaPlayer.pause()

        const animation = this.el.animate(
            [
                { transform: 'translateY(0%)' },
                { transform: 'translateY(150%)' },
            ],
            {
                duration: 500,
                easing: 'ease-in-out',
            }
        )

        await animation.finished
    }

    public destroy() {
        this.el.remove()
        this.removeSwupRules()
    }

    /**
     * Only for reloading the source page itself, see reloadOutlet().
     * From any other page, a fragment visit would swap just `#outlet` and leave the rest of that page in place.
     */
    private injectSwupRules() {
        window.swup.use(
            new SwupFragmentPlugin({
                rules: [
                    {
                        from: this.source,
                        to: this.source,
                        containers: ['#outlet'],
                    },
                ],
            })
        )
    }

    private removeSwupRules() {
        try {
            window.swup.unuse('SwupFragmentPlugin')
        } catch (e) {}
    }

    private reloadOutlet() {
        window.swup.navigate(this.source, {
            animate: false,
            history: 'replace',
        })
    }

    private removeDuplicateControls() {
        const mediaPlayer = this.el.querySelector('media-player')
        if (!mediaPlayer) return

        const controls = mediaPlayer.querySelectorAll('media-controls')

        // If there are duplicates, remove all except the last one (most recent)
        if (controls.length > 1) {
            controls.forEach((control, index) => {
                if (index < controls.length - 1) {
                    control.remove()
                }
            })
        }
    }

    public get isSourcePage() {
        return window.location.pathname === this.source
    }
}

export default FloatingPlayer
